import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import Color from "containers/admin/clients/client/theme-page/preview/sub-tabs/color/color";
import Fonts from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/fonts";
import Typography from "containers/admin/clients/client/theme-page/preview/sub-tabs/typography/typography";
import Button from "containers/admin/clients/client/theme-page/preview/sub-tabs/button/button";
import Banner from "containers/admin/clients/client/theme-page/preview/sub-tabs/banner/banner";
import Layout from "containers/admin/clients/client/theme-page/preview/sub-tabs/layout/layout";
import { PreviewToolbar } from "containers/admin/clients/client/theme-page/preview/preview-toolbar/preview-toolbar";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import PreviewSettings from "containers/admin/clients/client/theme-page/preview/preview-settings/preview-settings";
import toast from "components/partials/toast/toast";

import { useCurrentClient } from "hooks/api/use-clients";
import { saveTheme } from "hooks/api/use-theme";
import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

import { transformThemeDataOnSubmit } from "containers/admin/clients/client/theme-page/preview/helpers/formatters-on-post-data/formatters-on-post-data";
import { useInterceptNavigation } from "containers/admin/clients/client/theme-page/preview/hooks/use-intercept-navigation";

import { AppThemeData } from "containers/admin/clients/client/theme-page/preview/preview.types";

import {
  LINKS_TO_CHECK_BEFORE_REDIRECT,
  PREVIEW_FORM_DATA_VALIDATION_SCHEMA,
} from "containers/admin/clients/client/theme-page/preview/preview.constants";

const Preview = ({ colors, typography, fonts, button, banner, layout }: AppThemeData) => {
  const [currentTab, setCurrentTab] = useState("Colors");

  const {
    isEditMode,
    setIsEditMode,
    themeShouldBeUpdated,
    setThemeShouldBeUpdated,
    getUpdatedThemeContext,
    adobeFontsList,
    googleFontsList,
    setIsSettingsOpen,
  } = useThemePreviewContext();

  //modal
  const [isModalBeforeLeavingOpen, setIsModalBeforeLeavingOpen] = useState(false);
  const [nextRouteConfirm, setNextRouteConfirm] = useState<string>("");

  const navigate = useNavigate();

  const client = useCurrentClient();

  const tabHasError = (tabPath: string) => {
    return tabPath.toLowerCase() in methods.formState.errors;
  };

  //form
  const methods = useForm<AppThemeData>({
    mode: "onChange",
    defaultValues: { colors, fonts, typography, button, banner, layout },
    resolver: PREVIEW_FORM_DATA_VALIDATION_SCHEMA,
    shouldFocusError: true,
  });

  const { reset, getValues } = methods;

  const onSubmit = async () => {
    const allFormValues = getValues();

    const transformedThemeDataToSend = transformThemeDataOnSubmit(
      allFormValues,
      adobeFontsList,
      googleFontsList,
    );

    try {
      //get the updated theme data
      await saveTheme(client.id, transformedThemeDataToSend);

      //update form after saving data
      setThemeShouldBeUpdated(true);
    } catch (error) {
      console.error("Failed to submit theme:", error);

      toast.error({
        data: {
          title: "Failed to save theme",
          message: error.message,
        },
      });
    } finally {
      setIsEditMode(false);
    }
  };

  //modal before leaving
  const handleConfirm = () => {
    navigate(nextRouteConfirm);
    setIsModalBeforeLeavingOpen(false);
  };

  const openModalAndSetNavigateRoute = (route: string) => {
    isEditMode && setIsModalBeforeLeavingOpen(true);
    setNextRouteConfirm(route);
  };

  useInterceptNavigation(
    LINKS_TO_CHECK_BEFORE_REDIRECT,
    openModalAndSetNavigateRoute,
    isEditMode && methods.formState.isDirty,
  );

  //resetting data
  const getBackToPreviousThemeData = async () => {
    const newTheme = await getUpdatedThemeContext();

    reset(newTheme);
    setIsEditMode(false);
    setThemeShouldBeUpdated(false);
  };

  useEffect(() => {
    themeShouldBeUpdated && getBackToPreviousThemeData();
    // eslint-disable-next-line
  }, [themeShouldBeUpdated]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="flex flex-1 flex-col overflow-hidden h-full">
        <PreviewToolbar
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          tabHasError={tabHasError}
        />
        <div className="border-t border-zinc-200 flex flex-1 overflow-hidden">
          {currentTab === "Colors" && <Color />}
          {currentTab === "Fonts" && <Fonts />}
          {currentTab === "Typography" && <Typography />}
          {currentTab === "Button" && <Button />}
          {currentTab === "Banner" && <Banner />}
          {currentTab === "Layout" && <Layout />}
          <PreviewSettings onClose={() => setIsSettingsOpen(false)} />
        </div>
      </form>
      {isModalBeforeLeavingOpen && methods.formState.isDirty && (
        <ConfirmationDialog
          open
          onClose={() => setIsModalBeforeLeavingOpen(false)}
          onConfirm={handleConfirm}
          intent="warning"
          headline="Discard changes"
          message={
            <>
              This action will discard all changes made during this session.
              <br />
              Once discarded, these changes cannot be restored, and the theme configuration will
              revert to its previous data.
            </>
          }
        />
      )}
    </FormProvider>
  );
};

export default Preview;
